import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { TemplateManager } from "@/components/crm/TemplateManager";

type Props = {
  open: boolean;
  onClose: () => void;
  onTemplatesChanged?: () => void;
};

export function TemplateManagerDrawer({ open, onClose, onTemplatesChanged }: Props) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-stretch justify-end">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-label="Close template manager"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="relative flex h-full w-full max-w-[min(92vw,1200px)] flex-col border-l border-border bg-background shadow-2xl">
        <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Email</p>
            <h2 className="text-base font-semibold text-foreground">Template Manager</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border p-1.5 text-foreground/70 transition-all duration-200 hover:border-primary/70 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-3">
          <TemplateManager onTemplatesChanged={onTemplatesChanged} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
